export const T12InValuation = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-teal-50 border border-teal-200 rounded-full mb-6">
            <span className="text-teal-600 text-sm font-semibold">Valuation Impact</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            How T12 Drives Property Valuation
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Understanding the direct relationship between your T12 NOI and property value is essential for maximizing your sale price.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <div className="aspect-w-4 aspect-h-3 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://public.readdy.ai/ai/img_res/e8ee24a4ac0ab0d6ed477485719be0b9.jpg"
                alt="Property valuation infographic showing T12 NOI and cap rate relationship"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">The Income Capitalization Approach</h3>
              <p className="text-gray-700 mb-4">
                Commercial real estate investors use the income capitalization approach to value apartment buildings. This method directly ties your property's value to its T12 NOI.
              </p>
              
              <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-6 text-white">
                <div className="text-center">
                  <div className="text-sm opacity-90 mb-2">Valuation Formula</div>
                  <div className="text-3xl font-bold mb-4">Property Value = T12 NOI ÷ Cap Rate</div>
                  <div className="border-t border-white/30 pt-4 mt-4">
                    <div className="text-sm opacity-90 mb-2">Example</div>
                    <div className="text-xl">$183,206 ÷ 6.5% = $2,818,554</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Understanding Cap Rates</h3>
              <p className="text-gray-700 mb-4">
                The capitalization rate (cap rate) represents the expected rate of return on a real estate investment. It varies by market, property quality, and risk level.
              </p>
              
              <div className="space-y-3">
                <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold text-gray-900">Class A Properties</span>
                    <span className="text-teal-600 font-bold">4.5% - 6.0%</span>
                  </div>
                  <p className="text-sm text-gray-600">Newer, well-maintained, prime locations</p>
                </div>
                
                <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold text-gray-900">Class B Properties</span>
                    <span className="text-teal-600 font-bold">6.0% - 7.5%</span>
                  </div>
                  <p className="text-sm text-gray-600">Average condition, good locations</p>
                </div>
                
                <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold text-gray-900">Class C Properties</span>
                    <span className="text-teal-600 font-bold">7.5% - 10.0%</span>
                  </div>
                  <p className="text-sm text-gray-600">Older, needs work, secondary locations</p>
                </div>
              </div>
              
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Important:</strong> Higher cap rates mean lower property values. A property with $200,000 NOI is worth $4M at 5% cap rate but only $2.67M at 7.5% cap rate.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200 mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Cap Rate Sensitivity Analysis</h3>
          <p className="text-center text-gray-700 mb-8 max-w-3xl mx-auto">
            See how different cap rates affect your property's value with the same T12 NOI of $183,206
          </p>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="text-left py-4 px-4 font-bold text-gray-900">Cap Rate</th>
                  <th className="text-center py-4 px-4 font-bold text-gray-900">Market Type</th>
                  <th className="text-right py-4 px-4 font-bold text-gray-900">Property Value</th>
                  <th className="text-right py-4 px-4 font-bold text-gray-900">Value Difference</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="bg-green-50">
                  <td className="py-4 px-4 font-bold text-gray-900">5.0%</td>
                  <td className="text-center py-4 px-4 text-gray-700">Strong market, Class A</td>
                  <td className="text-right py-4 px-4 font-bold text-green-600">$3,664,120</td>
                  <td className="text-right py-4 px-4 font-bold text-green-600">+$845,566</td>
                </tr>
                <tr className="bg-green-50/50">
                  <td className="py-4 px-4 font-bold text-gray-900">5.5%</td>
                  <td className="text-center py-4 px-4 text-gray-700">Good market, Class A/B</td>
                  <td className="text-right py-4 px-4 font-bold text-green-600">$3,330,836</td>
                  <td className="text-right py-4 px-4 font-bold text-green-600">+$512,282</td>
                </tr>
                <tr className="bg-blue-50">
                  <td className="py-4 px-4 font-bold text-gray-900">6.0%</td>
                  <td className="text-center py-4 px-4 text-gray-700">Average market, Class B</td>
                  <td className="text-right py-4 px-4 font-bold text-blue-600">$3,053,433</td>
                  <td className="text-right py-4 px-4 font-bold text-blue-600">+$234,879</td>
                </tr>
                <tr className="bg-blue-50/50">
                  <td className="py-4 px-4 font-bold text-gray-900">6.5%</td>
                  <td className="text-center py-4 px-4 text-gray-700">Market baseline</td>
                  <td className="text-right py-4 px-4 font-bold text-gray-900">$2,818,554</td>
                  <td className="text-right py-4 px-4 font-bold text-gray-900">Baseline</td>
                </tr>
                <tr className="bg-yellow-50">
                  <td className="py-4 px-4 font-bold text-gray-900">7.0%</td>
                  <td className="text-center py-4 px-4 text-gray-700">Weaker market, Class B/C</td>
                  <td className="text-right py-4 px-4 font-bold text-yellow-600">$2,617,229</td>
                  <td className="text-right py-4 px-4 font-bold text-yellow-600">-$201,325</td>
                </tr>
                <tr className="bg-yellow-50/50">
                  <td className="py-4 px-4 font-bold text-gray-900">7.5%</td>
                  <td className="text-center py-4 px-4 text-gray-700">Soft market, Class C</td>
                  <td className="text-right py-4 px-4 font-bold text-orange-600">$2,442,747</td>
                  <td className="text-right py-4 px-4 font-bold text-orange-600">-$375,807</td>
                </tr>
                <tr className="bg-red-50">
                  <td className="py-4 px-4 font-bold text-gray-900">8.0%</td>
                  <td className="text-center py-4 px-4 text-gray-700">Distressed market/property</td>
                  <td className="text-right py-4 px-4 font-bold text-red-600">$2,290,075</td>
                  <td className="text-right py-4 px-4 font-bold text-red-600">-$528,479</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="mt-6 p-6 bg-teal-50 border-l-4 border-teal-500 rounded-r-lg">
            <p className="text-gray-700">
              <strong>Key Takeaway:</strong> The same property with identical T12 NOI can be worth $3.66M in a strong market (5% cap) or $2.29M in a distressed market (8% cap) - a difference of $1.37 million based solely on market cap rates.
            </p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Factors That Lower Cap Rates (Increase Value)</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-green-600"></i>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Prime Location</div>
                  <p className="text-sm text-gray-600">Strong job market, low crime, good schools</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-building-line text-green-600"></i>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Property Quality</div>
                  <p className="text-sm text-gray-600">Newer construction, excellent condition, modern amenities</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-team-line text-green-600"></i>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Strong Tenancy</div>
                  <p className="text-sm text-gray-600">High occupancy, quality tenants, long-term leases</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-line-chart-line text-green-600"></i>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Income Growth</div>
                  <p className="text-sm text-gray-600">Consistent rent increases, below-market rents with upside</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-list-3-line text-green-600"></i>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Clean Financials</div>
                  <p className="text-sm text-gray-600">Well-documented T12, consistent performance, no red flags</p>
                </div>
              </li>
            </ul>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Factors That Raise Cap Rates (Decrease Value)</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-red-600"></i>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Poor Location</div>
                  <p className="text-sm text-gray-600">High crime, declining area, limited job opportunities</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-tools-line text-red-600"></i>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Deferred Maintenance</div>
                  <p className="text-sm text-gray-600">Old systems, needed repairs, poor condition</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-door-open-line text-red-600"></i>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">High Vacancy</div>
                  <p className="text-sm text-gray-600">Chronic vacancies, tenant turnover, collection issues</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-arrow-down-line text-red-600"></i>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Declining Income</div>
                  <p className="text-sm text-gray-600">Falling rents, increasing expenses, negative trends</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-alert-line text-red-600"></i>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Financial Issues</div>
                  <p className="text-sm text-gray-600">Incomplete records, inconsistent T12, verification problems</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl p-8 text-white">
          <h3 className="text-2xl font-bold mb-6 text-center">Maximizing Your Property Value Through T12</h3>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-teal-400 mb-2">Step 1</div>
              <h4 className="font-bold mb-3">Optimize Your NOI</h4>
              <p className="text-sm text-gray-300">
                Increase income and reduce expenses over 6-12 months before selling. Every $10,000 NOI increase can add $150,000+ to your value.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-teal-400 mb-2">Step 2</div>
              <h4 className="font-bold mb-3">Document Everything</h4>
              <p className="text-sm text-gray-300">
                Maintain clean, accurate T12 records with supporting documentation. Strong financials can lower the cap rate buyers use by 0.5-1.0%.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-teal-400 mb-2">Step 3</div>
              <h4 className="font-bold mb-3">Time Your Sale</h4>
              <p className="text-sm text-gray-300">
                Sell when your T12 shows strong, consistent performance and market cap rates are favorable. Timing can impact value by 15-25%.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};